import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { rankInsights } from "@paisa/finance/insights"
import { createFileRoute } from "@tanstack/react-router"
import { InboxIcon } from "lucide-react"
import { useMemo } from "react"
import { FirstImportChoices } from "@/components/common/first-import-choices"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { useDashboard } from "@/hooks/use-dashboard"
import { useHousehold } from "@/hooks/use-household"
import { AccountsCard } from "./-home/accounts-card"
import { Hero } from "./-home/hero"
import { buildHeroAnswer, resolveToday } from "./-home/hero-answer"
import { InsightList } from "./-home/insight-list"
import { SetupChecklist } from "./-home/setup-checklist"
import { useInsightSources } from "./-home/use-insight-sources"
import {
  BudgetHealth,
  type ChartPoint,
  DebtWidget,
  KpiRow,
  RecentTransactions,
  TrendsChart,
} from "./-home/widgets"

export const Route = createFileRoute("/")({
  component: Dashboard,
})

function formatMonth(month: string) {
  const [y, m] = month.split("-")
  const date = new Date(Number(y), Number(m) - 1)
  return date.toLocaleDateString("en-IN", { month: "short" })
}

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-4" data-testid="home-skeleton">
      <Skeleton className="h-40 ring-1 ring-border-subtle md:h-48" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 ring-1 ring-border-subtle md:h-28" />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-24 ring-1 ring-border-subtle md:h-28" />
        ))}
      </div>
      <Skeleton className="h-64 ring-1 ring-border-subtle md:h-80" />
    </div>
  )
}

function Dashboard() {
  const fmt = useCurrencyFormat()
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const { data, isLoading, isError, error, refetch } = useDashboard()

  // Label the period the summary actually covers. When the current month has no
  // activity the API anchors to the latest month that does (#57); reflect that
  // here instead of always showing the (empty) current month.
  const summary = data?.summary
  const labelDate = summary ? new Date(summary.year, summary.month - 1, 1) : new Date()
  const monthLabel = labelDate.toLocaleDateString("en-IN", { month: "long", year: "numeric" })
  const subtitle = summary?.anchoredMonth ? `${monthLabel} · latest activity` : monthLabel

  const trends = data?.trends ?? []
  const recentTxns = data?.recentTransactions ?? []
  const netWorth = data?.netWorth
  const budgets = data?.budgetVsActual ?? []
  const hasTransactions = recentTxns.length > 0 || trends.length > 0
  const sources = useInsightSources(!!data && hasTransactions)
  const { data: household } = useHousehold()

  // "Today" is the server's household date, so a browser in another timezone
  // (or with a wrong clock) can't disagree about which month is current.
  const asOf = data?.asOf
  const today = useMemo(() => resolveToday(asOf), [asOf])
  const hero = useMemo(
    () => buildHeroAnswer({ summary, trends, hasTransactions, today }),
    [summary, trends, hasTransactions, today],
  )
  const insights = useMemo(
    () =>
      hasTransactions
        ? rankInsights({
            summary,
            trends,
            budgets,
            netWorth,
            upcoming: sources.upcoming,
            uncategorizedCount: sources.uncategorizedCount,
            investments: sources.investments,
            today,
            fmt,
          })
        : [],
    [hasTransactions, summary, trends, budgets, netWorth, sources, fmt, today],
  )

  if (isLoading) {
    return (
      <>
        <Header title="Dashboard" subtitle={subtitle} />
        <PageWrapper>
          <DashboardSkeleton />
        </PageWrapper>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header title="Dashboard" subtitle={subtitle} />
        <PageWrapper>
          <ErrorState message={(error as Error)?.message} onRetry={refetch} />
        </PageWrapper>
      </>
    )
  }

  // Nothing imported yet — the whole dashboard is otherwise a wall of "—" and
  // zeroes. Lead with the same three ingestion choices onboarding ends in
  // (PAISA-ING-01, PAISA-EMPTY-01) instead of an empty chart and stat cards.
  if (!hasTransactions && !netWorth) {
    return (
      <>
        <Header title="Dashboard" subtitle={subtitle} />
        <PageWrapper>
          <EmptyState
            icon={<InboxIcon className="size-8" />}
            title="Nothing imported yet"
            description="Bring your first month in and Paisa will answer where you stand."
            action={<FirstImportChoices />}
          />
        </PageWrapper>
      </>
    )
  }

  const chartData: ChartPoint[] = trends.map((t) => ({
    month: formatMonth(t.month),
    needs: Math.round(t.needs),
    wants: Math.round(t.wants),
    investments: Math.round(t.investments),
  }))

  const unreviewedCredits = (summary?.uncategorizedCredits ?? 0) > 0 && (
    <p className="text-caption text-text-muted" data-testid="unreviewed-credits">
      Unreviewed credits: {fmt(summary?.uncategorizedCredits ?? 0)} — counted toward surplus, not
      income, until categorised.
    </p>
  )

  const accounts = sources.accounts
  const hasAccounts = (accounts ?? []).some((a) => a.isActive !== false)
  const hasSidePanel = budgets.length > 0 || (data?.debtSummary?.count ?? 0) > 0 || hasAccounts

  return (
    <>
      <Header title="Dashboard" subtitle={subtitle} />
      <PageWrapper>
        <div className="flex min-w-0 flex-col gap-4 md:gap-5">
          <Hero answer={hero} />
          <InsightList insights={insights} isDesktop={isDesktop} />
          {sources.accounts && (
            <SetupChecklist
              hasAccounts={hasAccounts}
              uncategorizedCount={sources.uncategorizedCount}
              hasBudgets={budgets.length > 0}
              memberCount={household?.members?.length ?? 1}
            />
          )}

          {isDesktop ? (
            <>
              <KpiRow summary={summary} netWorth={netWorth} today={today} />
              {unreviewedCredits}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className={hasSidePanel ? "min-w-0 lg:col-span-2" : "min-w-0 lg:col-span-3"}>
                  <TrendsChart data={chartData} />
                </div>
                {hasSidePanel && (
                  <div className="flex min-w-0 flex-col gap-4">
                    <AccountsCard accounts={accounts} today={today} />
                    <BudgetHealth budgets={budgets} />
                    <DebtWidget debt={data?.debtSummary} />
                  </div>
                )}
              </div>
              <RecentTransactions txns={recentTxns} limit={7} />
            </>
          ) : (
            <>
              <RecentTransactions txns={recentTxns} limit={5} />
              <KpiRow summary={summary} netWorth={netWorth} today={today} />
              {unreviewedCredits}
              <TrendsChart data={chartData} />
              <AccountsCard accounts={accounts} today={today} />
              <BudgetHealth budgets={budgets} />
              <DebtWidget debt={data?.debtSummary} />
            </>
          )}
        </div>
      </PageWrapper>
    </>
  )
}
